import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { BreadcrumbService } from '@core/services/navigation/breadcrumb.service';

/**
 * Slim, always-mounted breadcrumb bar driven entirely by {@link BreadcrumbService}.
 *
 * Reproduces the legacy vex-breadcrumbs rendering (home icon + dot separators +
 * routerLink crumbs, last crumb as plain text) but reads its trail from the
 * centralized route-driven service instead of `@Input()`s. Static crumbs render
 * their i18n key through `translate`; dynamic crumbs render the resolved label,
 * falling back to the 'breadcrumbs.loading' placeholder so the bar is never empty.
 */
@Component({
  selector: 'app-breadcrumbs',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, MatIconModule, TranslatePipe],
  template: `
    <nav
      class="flex items-center gap-2 text-hint caption leading-none min-w-0 overflow-hidden"
      aria-label="breadcrumb">
      @for (crumb of visibleCrumbs(); track $index; let isFirst = $first; let isLast = $last) {
        @if (!isFirst) {
          <div class="w-1 h-1 bg-gray-400 rounded-full shrink-0"></div>
        }

        @if (!isLast || hideCurrent()) {
          <a
            [routerLink]="crumb.url"
            class="flex items-center min-w-0 no-underline transition-colors duration-200 hover:text-primary-600">
            @if (crumb.icon) {
              <mat-icon [svgIcon]="crumb.icon" class="icon-sm shrink-0"></mat-icon>
            } @else if (crumb.dynamic) {
              <span class="truncate">{{
                bc.getDynamicLabel(crumb.paramValue) || ('breadcrumbs.loading' | translate)
              }}</span>
            } @else {
              <span class="truncate">{{ crumb.key ?? '' | translate }}</span>
            }
          </a>
        } @else {
          <span class="flex items-center min-w-0 font-medium text-gray-700">
            @if (crumb.icon) {
              <mat-icon [svgIcon]="crumb.icon" class="icon-sm shrink-0"></mat-icon>
            } @else if (crumb.dynamic) {
              <span class="truncate">{{
                bc.getDynamicLabel(crumb.paramValue) || ('breadcrumbs.loading' | translate)
              }}</span>
            } @else {
              <span class="truncate">{{ crumb.key ?? '' | translate }}</span>
            }
          </span>
        }
      }
    </nav>
  `
})
export class BreadcrumbsComponent {
  protected readonly bc = inject(BreadcrumbService);

  /**
   * Drop the last crumb and render every remaining one as a link.
   *
   * Used by the compact secondary toolbar (#122), where the page title sits on
   * the same row as the trail: keeping the last crumb would print the page name
   * twice, side by side. With the current page gone from the trail, no crumb is
   * "you are here" any more — so they all become navigable, which is the whole
   * reason the last one was plain text.
   */
  readonly hideCurrent = input(false);

  protected readonly visibleCrumbs = computed(() => {
    const crumbs = this.bc.crumbs();
    // Never strip the trail down to nothing: a lone home crumb is still a
    // useful way back, and an empty <nav> would collapse the row height.
    if (!this.hideCurrent() || crumbs.length <= 1) return crumbs;
    return crumbs.slice(0, -1);
  });
}
